<template>
    <div
        ref="rootRef"
        class="navigation-tab flex"
        :class="{
            isActive,
            isCollapsable,
            isExpanded,
            isFull: isDisplayModeFull,
            isReduced: isDisplayModeReduced,
            isMobile: isDisplayMobile,
            isAlwaysDropdown: props.tab.isAlwaysDropdown,
            isHovering,
            isDumbHovering: dumbSyncIsHovering,
            isAnchoredBottom,
            isFocused,
            isHeaderMenu: headerMenu,
            isNotFullyVisible: !isFullyVisible,
        }"
        :style="tab.customStyle"
        :data-testid="tab.id"
        :tab-id="tab.id"
        tab-index="0"
        aria-role="button"
        @mouseenter="handleMouseEnter()"
        @mouseleave="handleMouseLeave"
        @click="handleTabClick"
    >
        <Component
            :is="getComponentType"
            ref="navigationTabRef"
            class="navigation-tab-item flex"
            :class="{hasBorder: tab.border, isMounted: isMountedDelayed}"
            :data-testid="`${tab.id}-Item`"
            v-bind="getComputedAttributes"
            :aria-label="tab.title"
            @click="handleClick"
        >
            <template v-if="navbarState.pending && tab.constraint != null">
                <Skeleton height="20px" :width="isDisplayModeReduced ? '20px' : '100%'" />
            </template>
            <template v-else>
                <div v-if="tab.icons?.mainIcon" class="navigation-tab-item_icon-wrapper">
                    <IconRenderer :svg-icon="tab.icons.mainIcon" :svg-active="isActive || dumbSyncIsHovering || isHovering" :svg-name="tab.title" />
                    <Transition name="fade-opacity" mode="out-in">
                        <NotificationBadge v-if="isNotificationBadgeVisibleForReduced" :count="getAllNotificationsCount" :offset="0" />
                    </Transition>
                </div>
                <div
                    v-if="isDisplayModeFull || isDisplayMobile || tab.showTitleAlways"
                    class="navigation-tab-item_title-wrapper"
                    :aria-label="tab.title"
                >
                    <div class="navigation-tab-item_title-wrapper_notifications-container">
                        <span class="navigation-tab-item_title-wrapper_value" :title="tab.title">{{ tab.title }}</span>

                        <Transition name="fade-opacity">
                            <NotificationBadge v-if="isNotificationBadgeVisibleForFull" :count="getAllNotificationsCount" :offset="0" />
                        </Transition>
                    </div>
                </div>
                <Transition name="fade-opacity">
                    <div
                        v-if="isCollapsable && (isDisplayModeFull || isDisplayMobile) && !hideCollapseOnFull"
                        :class="{isExpanded}"
                        class="navigation-tab-item_collapse-icon-wrapper flex"
                    >
                        <VJoyIcon name="chevron-down" color="neutral" />
                    </div>
                </Transition>
                <div v-if="tab.icons?.suffixIcon && (isDisplayModeFull || isDisplayMobile)" class="navigation-tab-item_icon-suffix-wrapper flex">
                    <IconRenderer
                        class="suffix-icon"
                        :svg-icon="tab.icons.suffixIcon"
                        :svg-active="isActive || dumbSyncIsHovering"
                        :svg-name="tab.title"
                    />
                </div>
            </template>
        </Component>
        <Teleport v-if="isCollapseMenuDisplaying && !tab.showTitleAlways" :disabled="!isDisplayModeReduced || isTransitioning" to="#teleports">
            <div ref="floatingRef" class="tab-floating-root" :style="getComputedPositioning">
                <Transition :name="getTransitionMode">
                    <ScrollOverflowIndicators
                        v-if="
                            ((isDisplayModeReduced || props.tab.isAlwaysDropdown) && isPositioned && !isTransitioning) ||
                            (!isDisplayModeReduced && !props.tab.isAlwaysDropdown)
                        "
                        ref="tabChildrenRef"
                        :data-placement="placement"
                        :class="[
                            ,
                            {
                                isExpanded: isCollapsable && isExpanded,
                                isReduced: isDisplayModeReduced,
                                isTransitioning,
                                isMobile: isDisplayMobile,
                                isAnchoredBottom,
                                isEmpty: !tab.children?.length,
                                isAlwaysDropdown: props.tab.isAlwaysDropdown,
                                fromSSR: fromSSR,
                            },
                        ]"
                        class="navigation-tab-children flex"
                        @mouseenter="handleMouseEnter(true)"
                        @mouseleave="handleMouseLeave"
                    >
                        <div v-if="getChildren" class="navigation-tab-children_list flex">
                            <NavigationTabItemChild
                                v-for="childTab of getChildren"
                                :key="childTab.id"
                                :tab="childTab"
                                :have-siblings="getChildren.length > 1"
                                :is-anchored-bottom="isAnchoredBottom"
                                :is-always-dropdown="props.tab.isAlwaysDropdown"
                                :selector="props.selector"
                                :placement="placement"
                            />
                        </div>
                    </ScrollOverflowIndicators>
                </Transition>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
    import type {AnchorHTMLAttributes, StyleValue} from 'vue';
    import {computed, nextTick, onMounted, ref, toRefs, useTemplateRef, watch} from 'vue';
    import {autoPlacement, offset, type Placement} from '@floating-ui/dom';
    import {autoUpdate, shift, useFloating} from '@floating-ui/vue';
    import {IconRenderer, Skeleton} from '@navbar-logged/common/ui';
    import {NotificationBadge} from '@navbar-logged/features/notifications';
    import {useNavbarStateStore} from '@navbar-registry';
    import {useHydration, useNuxtApp} from 'nuxt/app';
    import {storeToRefs} from 'pinia';
    import {ScrollOverflowIndicators} from '#components';
    import {useDebounceMount, onClickOutside, useDisplayMobile, useIntersectionObserver, useMouseHover, useScrollContainer} from '@malt/utils';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {useAsideMenuStore} from '../aside-menu.store';
    import type {NavigationTab} from '../aside-menu.types';
    import {useDisplayModeStore, useExpandedTabsStore} from '../stores';
    import NavigationTabItemChild from './NavigationTabItemChild.vue';

    const props = defineProps<{
        /** Main tab object */
        tab: NavigationTab<string>;
        /** Hide children on Full mode for specific tabs (ex: Language selection) */
        hideCollapseOnFull?: boolean;
        selector?: boolean;
        headerMenu?: boolean;
    }>();

    useHydration(
        'tabItem',
        () => {
            return {
                fromSSR: true,
            };
        },
        () => {},
    );

    const nuxtApp = useNuxtApp();
    const fromSSR = ref<boolean | undefined>(nuxtApp.payload.tabItem?.fromSSR || import.meta.server);

    const {tab} = toRefs(props);

    const navbarState = useNavbarStateStore();
    const {isDisplayMobile} = useDisplayMobile();

    const asideMenuStore = useAsideMenuStore();
    const {currentActiveRoute} = storeToRefs(asideMenuStore);

    const expandedTabsStore = useExpandedTabsStore();

    const displayModeStore = useDisplayModeStore();
    const {isDisplayModeFull, isDisplayModeReduced} = storeToRefs(displayModeStore);

    const rootRef = useTemplateRef('rootRef');
    const navigationTabRef = useTemplateRef<HTMLElement>('navigationTabRef');
    const tabChildrenRef = useTemplateRef('tabChildrenRef');
    const floatingRef = useTemplateRef('floatingRef');

    const {isFullyVisible} = useIntersectionObserver(rootRef, () => {}, {
        immediate: true,
        options: {root: document?.querySelector('#navigation-tabs-container')},
    });

    /** Dynamic component type */
    const getComponentType = computed<'div' | 'a'>(() => (tab.value.url ? 'a' : 'div'));

    // - Notifications
    const getNotificationsCount = computed<number>(() => {
        const baseCount = props.tab.notificationsCount ?? 0;
        if (props.tab.children?.length) {
            return props.tab.children.reduce((acc, value) => (acc += value.notificationsCount ?? 0), 0) + baseCount;
        } else {
            return baseCount;
        }
    });

    const getAllNotificationsCount = computed(() => getNotificationsCount.value);

    const isNotificationBadgeVisibleForReduced = computed(() => isDisplayModeReduced.value && getNotificationsCount.value > 0);
    const isNotificationBadgeVisibleForFull = computed(() => getNotificationsCount.value > 0 && !isExpanded.value);

    // - Hovering reduced mode

    const clickModeEnabled = computed(
        () => (triggerMode.value === 'click' && !isDisplayModeReduced.value) || (tab.value.isAlwaysDropdown && isDisplayMobile.value),
    );
    const triggerMode = computed(() => props.tab.triggerMode ?? 'hover');

    const isFocused = computed(() => clickModeEnabled.value && isHovering.value);

    function handleTabClick() {
        if (clickModeEnabled.value) {
            isHovering.value = !isHovering.value;
        }
    }

    onClickOutside(
        navigationTabRef,
        () => {
            if (clickModeEnabled.value) {
                isHovering.value = false;
            }
        },
        {
            ignore: [floatingRef],
            detectIframe: true,
        },
    );

    /** Keep this for hover effect */
    const {isHovering: dumbSyncIsHovering} = useMouseHover();
    const isHovering = ref(false);
    const isHidingTransition = ref(false);
    const isAnchoredBottom = ref(false);
    let timeOutHovering: number | null = null;

    const {containerWidth} = useScrollContainer({target: navigationTabRef});

    const computedPlacement = computed<Placement>(() => {
        if (props.tab.dropdownPlacement && !isDisplayModeReduced.value) {
            return props.tab.dropdownPlacement;
        }
        return 'right-start';
    });
    const isTransitioning = ref(false);

    const {floatingStyles, isPositioned, placement} = useFloating(navigationTabRef, floatingRef, {
        whileElementsMounted: autoUpdate,
        placement: computedPlacement,
        middleware: [
            autoPlacement(() => {
                if ((computedPlacement.value.startsWith('bottom') || computedPlacement.value.startsWith('top')) && !isDisplayModeReduced.value) {
                    return {padding: 150, allowedPlacements: ['top', 'bottom']};
                } else {
                    return {padding: 150, allowedPlacements: ['right-start', 'right-end', 'top', 'bottom']};
                }
            }),
            shift(),
            offset((state) => {
                if ((state.placement.startsWith('bottom') || state.placement.startsWith('top')) && !isDisplayModeReduced.value) {
                    return {
                        mainAxis: 4,
                    };
                } else {
                    return {
                        mainAxis: 16,
                        alignmentAxis: -4,
                    };
                }
            }),
        ],
        open: isHovering,
    });

    const getComputedPositioning = computed<StyleValue>(() => {
        if ((isDisplayModeReduced.value || props.tab.isAlwaysDropdown) && !isTransitioning.value && isMountedDelayed.value) {
            return {
                ...floatingStyles.value,
                width:
                    placement.value.startsWith('bottom') || (placement.value.startsWith('top') && !isDisplayModeReduced.value)
                        ? `${containerWidth.value}px`
                        : '',
            };
        } else {
            return {};
        }
    });

    const isCollapseMenuDisplaying = computed(
        () => isCollapsable.value || isDisplayModeReduced.value || props.tab.isAlwaysDropdown || tab.value.id !== 'Language',
    );

    function handleMouseEnter(isPanel = false) {
        if ((isDisplayModeReduced.value || props.tab.isAlwaysDropdown) && !clickModeEnabled.value) {
            if (isPanel && isHidingTransition.value) {
                return;
            }
            showChildrenInReduced();
        }
    }
    function handleMouseLeave(event: MouseEvent) {
        if (event.relatedTarget instanceof HTMLElement) {
            if (event.relatedTarget.isEqualNode(navigationTabRef.value) || event.relatedTarget.isEqualNode(tabChildrenRef.value?.$el)) {
                return;
            }
        }
        if ((isDisplayModeReduced.value || props.tab.isAlwaysDropdown) && !clickModeEnabled.value) {
            if (timeOutHovering) {
                window.clearTimeout(timeOutHovering);
            }

            isHidingTransition.value = true;
            setTimeout(() => {
                isHidingTransition.value = false;
            }, 300);
            isHovering.value = false;
        }
    }

    function showChildrenInReduced() {
        if (timeOutHovering) {
            window.clearTimeout(timeOutHovering);
        }
        timeOutHovering = window.setTimeout(() => {
            isHovering.value = true;
        }, 150);
    }

    // - Collapse and expand tabs

    const {isMounted: isMountedDelayed} = useDebounceMount(100, {mode: 'client'});
    useDebounceMount(500, {mode: 'client'}, () => {
        fromSSR.value = false;
    });

    const isCollapsable = computed<boolean>(() => !!tab.value.children?.length && !props.tab.isAlwaysDropdown);
    const isExpanded = computed<boolean>(
        () => expandedTabsStore.expandedTabs.includes(tab.value.id) && (isDisplayModeFull.value || isDisplayMobile.value),
    );

    let expandTimeOut: number | null = null;

    async function onExpandChange(fromDisplayMode = false) {
        const transitionDuration = 200;
        if (fromDisplayMode) {
            // Panel is collapsing during navbar transition to reduced mode
            const panel = tabChildrenRef.value?.$el as HTMLElement;
            if (panel && expandedTabsStore.expandedTabs.includes(tab.value.id)) {
                const scrollContainer = document.getElementById('navigation-tabs-container');
                scrollContainer?.scrollTo({top: scrollContainer.scrollTop - panel.scrollHeight, behavior: 'smooth'});

                // Panel is closing
                isTransitioning.value = true;
                panel.style.maxHeight = `${panel.scrollHeight}px`;

                // Prevent animation on first mount
                panel.style.transition = 'max-height var(--joy-transition-duration-default) ease-out';
                window.setTimeout(() => {
                    panel.style.maxHeight = '0';
                }, 1);
                window.setTimeout(() => {
                    isTransitioning.value = false;
                }, transitionDuration);
            } else if (panel) {
                panel.style.maxHeight = `0px`;
                window.setTimeout(() => {
                    panel.style.maxHeight = 'none';
                }, transitionDuration);
            }
        } else if (isDisplayModeFull.value || isDisplayMobile.value) {
            await nextTick();
            const panel = tabChildrenRef.value?.$el as HTMLElement;
            if (panel) {
                if (expandTimeOut) {
                    window.clearTimeout(expandTimeOut);
                }
                /** Avoid fixed size after transition */
                if (!isExpanded.value) {
                    if (!fromSSR.value) {
                        // Collapse is closing
                        if (isMountedDelayed.value) {
                            panel.style.maxHeight = `${panel.scrollHeight}px`;
                            // Prevent animation on first mount
                            // 200ms

                            panel.style.transition = 'max-height var(--joy-transition-duration-default) ease-out';
                        }
                        if (!fromSSR.value) {
                            window.setTimeout(() => {
                                panel.style.maxHeight = '0';
                            }, 1);
                        }
                    }
                } else if (expandedTabsStore.expandedTabs.includes(tab.value.id)) {
                    // Collapse is opening
                    if (isMountedDelayed.value) {
                        // Prevent animation on first mount
                        panel.style.transition = 'max-height var(--joy-transition-duration-default) ease-out';
                    }
                    if (!fromSSR.value) {
                        panel.style.maxHeight = `${panel.scrollHeight}px`;
                    }
                    expandTimeOut = window.setTimeout(() => {
                        panel.style.maxHeight = 'none';
                        panel.style.transition = '';
                    }, transitionDuration);
                }
            }
        }
    }

    function handleClick() {
        if (isCollapsable.value && (isDisplayModeFull.value || isDisplayMobile.value) && !props.hideCollapseOnFull) {
            expandedTabsStore.toggleExpandTab(tab.value.id);
        } else if (tab.value.action && typeof tab.value.action === 'function') {
            tab.value.action();
        }
    }

    watch(isExpanded, () => onExpandChange());
    watch(isDisplayModeReduced, () => onExpandChange(true));
    watch(isDisplayMobile, () => {
        isHovering.value = false;
    });
    onMounted(onExpandChange);

    // - Active state

    const isActive = computed<boolean>(() => currentActiveRoute.value === tab.value.id || isAnyChildActive.value);
    const isAnyChildActive = computed<boolean>(() => !!tab.value.children?.some((child) => isTabActive(child.id)));

    function isTabActive(id: string) {
        return currentActiveRoute.value === id;
    }

    // - Attributes
    const getComputedAttributes = computed<Partial<AnchorHTMLAttributes>>(() => {
        if (!isCollapsable.value && tab.value.url) {
            return {
                href: tab.value.url.href,
                ...(tab.value.url.blank && {
                    target: '_blank',
                    rel: 'noopener noreferer',
                }),
                ...tab.value.customAttrs,
            };
        } else {
            return {};
        }
    });

    const getTransitionMode = computed<'fade-scale' | 'default'>(() => {
        if (isExpanded.value) {
            return 'default';
        } else {
            return 'fade-scale';
        }
    });

    // - Children

    const getChildren = computed(() => {
        if (isDisplayModeReduced.value && !props.tab.children?.length && !props.selector) {
            return [tab.value];
        } else {
            return props.tab.children;
        }
    });

    // Type-check in unit tests
    defineExpose({
        isCollapsable,
        isExpanded,
        isActive,
        isAnyChildActive,
        getComponentType,
    });
</script>

<style lang="scss" scoped>
    .tab-floating-root {
        z-index: 10000;
    }

    .navigation-tab {
        flex-flow: column nowrap;
        width: 100%;
        overflow: hidden;

        $r: &;

        padding: 2px var(--joy-core-spacing-2);
        flex: 0 0 auto;
        scroll-margin: calc(var(--layout-padding-top) + 24px);

        &-item {
            height: 48px;
            flex-flow: row nowrap;
            align-items: center;
            flex: 0 0 auto;
            padding: 0 var(--joy-core-spacing-4);
            text-decoration: none;
            color: var(--joy-color-text-default);
            border-radius: var(--joy-core-radius-3);
            font-size: var(--joy-font-size-primary-300);
            transition:
                box-shadow var(--joy-transition-duration-default) ease,
                background-color var(--joy-transition-duration-default) ease;
            background-color: transparent;
            cursor: pointer;

            &.isMounted {
                transition:
                    background-color var(--joy-transition-duration-default) ease-out,
                    padding var(--joy-transition-duration-long) ease;
            }

            &.hasBorder {
                border: 1px var(--joy-color-neutral-30) solid;
            }

            &_icon-wrapper {
                flex: 0 0 auto;
                display: flex;
                justify-content: center;

                > * {
                    flex: 0 0 auto;
                }
            }

            &_custom-icon-wrapper {
                flex: 0 0 auto;
            }

            &_title-wrapper {
                display: flex;
                flex: 1 1 auto;
                color: var(--joy-color-neutral-60);
                margin-left: var(--joy-core-spacing-2);
                line-height: 1;
                max-width: 100%;
                position: relative;
                align-items: center;

                &_notifications-container {
                    position: relative;
                    display: flex;
                    flex-flow: row nowrap;
                    max-width: 100%;
                    flex: 0 1 auto;
                    min-width: 0;
                    align-items: center;

                    span {
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        max-width: 100%;
                        display: inline-block;
                        line-height: 1.3;
                        flex-shrink: 1;
                        flex-grow: 0;
                        min-width: 0;

                        // Prevent layout shift when font become bold
                        &::before {
                            display: block;
                            content: attr(title);
                            font-weight: var(--joy-font-weight-bold);
                            height: 0;
                            overflow: hidden;
                            visibility: hidden;
                        }
                    }

                    .suffix-icon {
                        flex: 0 0 auto;
                        vertical-align: text-top;
                        margin-left: 6px;
                    }
                }
            }

            &_collapse-icon-wrapper {
                flex: 0 0 auto;
                margin-left: var(--joy-core-spacing-2);
                transition: transform var(--joy-transition-duration-default) ease-out;
                transform-origin: center;
                align-items: center;

                joy-icon {
                    height: 18px;
                    color: var(--joy-color-neutral-60);
                }

                &.isExpanded {
                    transform: rotate(-180deg);
                }
            }

            &_icon-suffix-wrapper {
                flex: 0 0 auto;
                margin-left: var(--joy-core-spacing-2);
            }
        }

        &-children {
            flex-flow: column nowrap;
            max-height: 0;
            border-radius: var(--joy-core-radius-3);

            $child: &;

            background-color: var(--joy-color-background-default);
            flex: 0 0 auto;

            $p: &;

            &.isReduced,
            &.isAlwaysDropdown {
                max-height: 420px;
                max-width: none;
                z-index: 1000;
                box-shadow:
                    rgb(0 0 0 / 16%) 0 10px 36px 0,
                    rgb(0 0 0 / 6%) 0 0 0 1px;
                border-radius: var(--joy-core-radius-3);
                background-color: var(--joy-color-background-default);
                padding: 2px var(--joy-core-spacing-1);

                #{$p}_list {
                    overflow-y: auto;
                    scrollbar-width: none;
                    gap: 2px;
                }

                &::before {
                    content: '';
                    position: absolute;
                    background: transparent;
                    height: 100%;
                    width: 20px;
                    right: 100%;
                    z-index: 10;
                }

                &:not(.isEmpty)::after {
                    content: '';
                    position: absolute;
                    background: transparent;
                    height: 30px;
                    width: 48px;
                    right: 100%;
                    z-index: 10;
                }

                &:not(.isAnchoredBottom) {
                    &::before {
                        top: 0;
                    }
                }

                &[data-placement^='bottom'],
                &[data-placement^='top'] {
                    &::before {
                        content: '';
                        height: 8px;
                        width: 100%;
                        right: 0;
                        top: auto;
                    }
                }

                &[data-placement^='bottom'] {
                    &::before {
                        bottom: 100%;
                    }
                }

                &[data-placement^='top'] {
                    &::before {
                        top: 100%;
                    }
                }

                &[data-placement='right-end'] {
                    &::after {
                        transform: rotate(-45deg);
                        transform-origin: bottom right;
                        bottom: 70px;
                    }
                }

                &[data-placement='right-start'] {
                    &::after {
                        transform: rotate(45deg);
                        transform-origin: top right;
                        top: 70px;
                    }
                }
            }

            &_reduced-title {
                height: 48px;
                flex: 0 0 auto;
                flex-flow: row nowrap;
                align-items: center;
                padding: 0 var(--joy-core-spacing-4);
                text-decoration: none;
                color: var(--joy-color-neutral-60);
                font-weight: var(--joy-font-weight-bold);
                transition:
                    background-color 0.15s ease-out,
                    padding var(--joy-transition-duration-default) ease;

                span {
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                &.isEmpty {
                    border-radius: var(--joy-core-radius-3);
                    padding: 0 var(--joy-core-spacing-6);
                }

                &:not(.isEmpty) {
                    cursor: default !important;
                }
            }

            &_list {
                flex-flow: column nowrap;
                background-color: var(--joy-color-background-default);
                border-radius: 0 0 10px;
            }

            &.isExpanded.fromSSR {
                max-height: none;
            }
        }

        &.isCollapsable.isReduced,
        &.isCollapsable.isAlwaysDropdown {
            #{$r}-children {
                min-width: 200px;
            }
        }

        &:active,
        &.isFocused {
            #{$r}-item {
                box-shadow: 0 0 5px inset rgb(30 30 30 / 10%);
            }
        }

        // Active/not Active state

        &:not(.isActive) {
            &.isFull {
                #{$r}-item:hover {
                    background-color: var(--joy-color-neutral-20);
                }
            }

            &.isReduced.isHovering,
            &.isReduced.isDumbHovering,
            &.isAlwaysDropdown.isHovering,
            &.isCollapsable.isReduced:hover {
                #{$r}-item {
                    background-color: var(--joy-color-neutral-20);
                }
            }
        }

        &.isActive {
            &.isExpanded {
                #{$r}-item {
                    &:hover {
                        background-color: var(--joy-color-neutral-20);
                    }

                    &_title-wrapper {
                        font-weight: var(--joy-font-weight-bold);
                    }
                }
            }

            &:not(.isExpanded) {
                #{$r}-item {
                    background-color: var(--joy-color-secondary-10);

                    &_title-wrapper {
                        font-weight: var(--joy-font-weight-bold);
                    }
                }
            }
        }

        // - Reduced

        &.isReduced,
        &.isAlwaysDropdown {
            justify-content: center;
            overflow: visible;
            padding: 2px var(--joy-core-spacing-2);

            #{$r}-item {
                padding: 0 calc(var(--joy-core-spacing-4));
            }
        }

        &.isReduced:not(.isHeaderMenu) {
            #{$r}-item {
                &_icon-wrapper {
                    .svg-icon-wrapper,
                    .icon-img-wrapper,
                    :deep(.joy-avatar__wrapper) {
                        transition:
                            transform var(--joy-transition-duration-default) ease,
                            margin var(--joy-transition-duration-default) ease;
                        transform: scale(1.2);
                        margin-left: 2px;
                    }
                }
            }
        }

        &.isCustom {
            #{$r}-item {
                justify-content: center;
                padding: 0 var(--joy-core-spacing-4) !important;
            }
        }
    }
</style>
